import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Trophy, Calendar, TrendingUp } from 'lucide-react';
import { SongCard } from '../../components/song/SongCard.js';
import { songsService } from '../../services/songs.service.js';

type ChartPeriod = 'weekly' | 'monthly';

function ChartsSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: 20 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-3 py-2.5">
          <div className="w-6 h-5 bg-surface-hover rounded" />
          <div className="flex-1 space-y-1.5">
            <div className="h-4 bg-surface-hover rounded w-1/3" />
            <div className="h-3 bg-surface-hover rounded w-1/5" />
          </div>
          <div className="w-16 h-3 bg-surface-hover rounded" />
        </div>
      ))}
    </div>
  );
}

export default function ChartsPage() {
  const [period, setPeriod] = useState<ChartPeriod>('weekly');

  const { data: weeklyData, isLoading: weeklyLoading } = useQuery({
    queryKey: ['charts', 'weekly'],
    queryFn: () => songsService.getWeeklyCharts(),
    enabled: period === 'weekly',
  });

  const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
    queryKey: ['charts', 'monthly'],
    queryFn: () => songsService.getMonthlyCharts(),
    enabled: period === 'monthly',
  });

  const songs = period === 'weekly' ? (weeklyData?.data ?? []) : (monthlyData?.data ?? []);
  const isLoading = period === 'weekly' ? weeklyLoading : monthlyLoading;

  return (
    <>
      <Helmet>
        <title>Charts {period === 'weekly' ? 'Semanal' : 'Mensual'} | LyricsHub</title>
        <meta
          name="description"
          content={`Top canciones de la ${period === 'weekly' ? 'semana' : 'mes'} en LyricsHub. Descubre las canciones mas populares.`}
        />
      </Helmet>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-text-primary flex items-center gap-2">
          <Trophy size={28} className="text-primary" />
          Charts
        </h1>
        <p className="text-text-secondary mt-1">Las canciones mas populares del momento</p>
      </div>

      {/* Period Tabs */}
      <div className="flex gap-1 border-b border-border mb-6">
        <button
          onClick={() => setPeriod('weekly')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            period === 'weekly'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <Calendar size={16} />
          Semanal
        </button>
        <button
          onClick={() => setPeriod('monthly')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            period === 'monthly'
              ? 'border-primary text-primary'
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <TrendingUp size={16} />
          Mensual
        </button>
      </div>

      {/* Charts List */}
      {isLoading ? (
        <ChartsSkeleton />
      ) : songs.length === 0 ? (
        <p className="text-center text-text-muted py-12">No hay datos de charts disponibles.</p>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden divide-y divide-border">
          {songs.map((song: any, i: number) => (
            <div key={song.id} className="flex items-center gap-1">
              {/* Position Number */}
              <div className="w-12 shrink-0 flex items-center justify-center">
                <span
                  className={`text-lg font-bold ${
                    i === 0
                      ? 'text-yellow-500'
                      : i === 1
                        ? 'text-gray-400'
                        : i === 2
                          ? 'text-amber-600'
                          : 'text-text-muted'
                  }`}
                >
                  {i + 1}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <SongCard
                  slug={song.slug}
                  title={song.title}
                  artistName={song.artistName}
                  artistSlug={song.artistSlug}
                  coverUrl={song.coverUrl}
                  genreName={song.genreName}
                  viewCount={song.viewCount}
                  likeCount={song.likeCount}
                  variant="list"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
